import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import { Card, Btn, Modal, Table, Spinner, ErrBox } from '../../../components/UI';
import { fmtRp } from '../../../utils/format';
import { assetsAPI } from '../../../utils/api';

export default function DepreciationTab() {
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [err, setErr] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ depreciation_year: new Date().getFullYear(), depreciation_month: new Date().getMonth() + 1 });
  const [report, setReport] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [result, setResult] = useState(null);

  const inputStyle = { padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

  const loadReport = async (year) => {
    setReportLoading(true);
    try {
      const r = await assetsAPI.getDepReport(year ? `?year=${year}` : '');
      setReport(r.data || []);
    } catch (e) { setErr(e.message); }
    finally { setReportLoading(false); }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const handleCalculate = async () => {
    setCalculating(true);
    setErr('');
    try {
      const r = await assetsAPI.calculateMonthly(form);
      setResult(r.data);
      setShowModal(false);
      loadReport(form.depreciation_year);
    } catch (e) {
      setErr(e.message);
    } finally {
      setCalculating(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Perhitungan Depresiasi Aset</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6B7280" }}>Hitung depresiasi otomatis metode garis lurus</p>
          </div>
          <Btn icon={<Calculator size={14}/>} onClick={() => setShowModal(true)}>Hitung Depresiasi</Btn>
        </div>
        {err && <div style={{marginBottom: 12}}><ErrBox msg={err} onRetry={loadReport}/></div>}
        {result && (
          <div style={{ padding: "12px 16px", background: "#ECFDF5", border: "1px solid #6EE7B7", borderRadius: 8, marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#047857", fontWeight: 600 }}>
              ✅ {result.message || 'Perhitungan depresiasi berhasil'}
            </p>
          </div>
        )}
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Laporan Depresiasi ({report.length})</h3>
          <select
            value={form.depreciation_year}
            onChange={e => { setForm({...form, depreciation_year: Number(e.target.value)}); loadReport(e.target.value); }}
            style={{ padding: "8px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", background: "white" }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {reportLoading ? <Spinner/> : (
          <Table pageSize={10} cols={[
            { key: "code", label: "Kode Aset", nowrap: true },
            { key: "name", label: "Nama Aset" },
            { key: "category", label: "Kategori" },
            { key: "acquisition_value", label: "Nilai Perolehan", align: "right",
              render: v => <span style={{fontFamily: "monospace", fontSize: 12}}>{fmtRp(v)}</span> },
            { key: "total_depreciation", label: "Total Depresiasi", align: "right",
              render: v => <span style={{fontFamily: "monospace", fontSize: 12, color: "#DC2626"}}>{fmtRp(v || 0)}</span> },
            { key: "book_value", label: "Nilai Buku", align: "right",
              render: v => <span style={{fontFamily: "monospace", fontSize: 12, color: "#047857"}}>{fmtRp(v)}</span> },
          ]} data={report} emptyMsg="Belum ada data depresiasi"/>
        )}
      </Card>

      {showModal && (
        <Modal title="Hitung Depresiasi" onClose={() => setShowModal(false)}>
          <div>
            <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>
              Hitung depresiasi untuk periode yang dipilih. Depresiasi dihitung menggunakan metode garis lurus.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Tahun</label>
                <select
                  value={form.depreciation_year}
                  onChange={e => setForm({...form, depreciation_year: Number(e.target.value)})}
                  style={inputStyle}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Bulan</label>
                <select
                  value={form.depreciation_month}
                  onChange={e => setForm({...form, depreciation_month: Number(e.target.value)})}
                  style={inputStyle}>
                  {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 24 }}>
              <Btn variant="secondary" onClick={() => setShowModal(false)}>Batal</Btn>
              <Btn icon={<Calculator size={14}/>} onClick={handleCalculate} disabled={calculating}>
                {calculating ? "Menghitung…" : "Hitung"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
